import { ref, watch, toValue, type MaybeRefOrGetter, type Ref } from 'vue'

import type { EntityId } from './types'

/**
 * `expand-row-keys` for a table whose first row should already be open, so the record's
 * children are readable without a click. Bind the result and pass `expandable` as the same
 * condition the table uses; the keys are emptied when the list or that flag goes away.
 *
 * **It opens the first row on arrival and then leaves the table alone.** Element Plus
 * replaces its open rows with exactly what this array names, and the user's own expansions
 * are never written back into it — so re-assigning an equal-looking array after a refetch
 * would collapse whatever they had opened. The assignment is therefore skipped while the
 * first row is still the same row. Do not "simplify" that check away, and do not reach for
 * this to *control* which rows are open: that is `useExpandAllRows`.
 */
export function useAutoExpandFirstRow<T extends { id: EntityId }>(
  items: MaybeRefOrGetter<T[] | undefined>,
  expandable: MaybeRefOrGetter<boolean>
): Ref<EntityId[]> {
  const expandedRowKeys = ref<EntityId[]>([])

  watch(
    [() => toValue(items), () => toValue(expandable)],
    ([data, expand]) => {
      const first = expand ? data?.[0] : undefined
      const next = first ? [first.id] : []

      // Same first row: leave the array — and with it whatever the user has open — alone.
      if (next[0] === expandedRowKeys.value[0]) return

      expandedRowKeys.value = next
    },
    { immediate: true }
  )

  return expandedRowKeys
}
